/**
 * ProgressChart.jsx - a child's 208 skills, for the person who is raising them.
 *
 * Replaces client/public/tools/speechformation-chart-visual.html, which was a
 * mockup: it invented its own data, it drew 212 boxes while labelling them 208
 * (it gave component E to every level of phases 7 and 8, where the framework
 * gives it to 7.3-7.5 and 8.3-8.6), and its one grey meant both "not started"
 * and "nothing can measure this".
 *
 * WHAT THIS COMPONENT DOES AND DOES NOT DECIDE
 * It draws. Every colour, every count and the shape of the chart itself come
 * from the engine via chartModel.js; there is no scoring here and there must
 * never be. If a number on this screen looks wrong, it is wrong in scoring.js
 * or in the box_score row, and fixing it here would only hide it.
 *
 * WHO IT IS FOR
 * A parent, on a phone, who wants to know whether this is working. So:
 *   - plain language throughout - see parentLanguage.js
 *   - nothing reads as failure, because there is no failure in this product
 *   - the high-water mark is what is painted, so the chart never appears to go
 *     backwards over what is usually the noise in a ten-play window
 *   - a box that has slipped is an invitation to play, in the corner, in amber
 *   - "green in two different games" is called out, because that distinction is
 *     the point of the whole framework and it is invisible if you do not say it
 */

import { useEffect, useMemo, useState } from 'react';
import { engineApi } from '../../utils/api';
import { buildChartModel, STATES } from '../../chart/chartModel';
import {
  stateLanguage, stepLabel, skillCount, READY_AGAIN,
} from '../../chart/parentLanguage';
import { COLORS } from '../../engine/framework';
import './ProgressChart.css';

const COLOUR_HEX = {
  green: COLORS.green.hex,
  purple: COLORS.purple.hex,
  blue: COLORS.blue.hex,
  yellow: COLORS.yellow.hex,
  red: COLORS.red.hex,
};

/** Legend order: best first, then the three things that are not scores. */
const LEGEND = [
  'green', 'purple', 'blue', 'yellow', 'red',
  STATES.IN_PROGRESS, STATES.NOT_STARTED, STATES.OBSERVATIONAL, STATES.NOT_ASSESSABLE,
];

/* ------------------------------------------------------------------ pieces */

function Ring({ percent }) {
  const r = 34;
  const c = 2 * Math.PI * r;
  return (
    <div className="pc-ring">
      <svg width="84" height="84" viewBox="0 0 84 84" aria-hidden="true">
        <circle cx="42" cy="42" r={r} fill="none" stroke="rgba(255,255,255,.22)" strokeWidth="8" />
        <circle
          cx="42" cy="42" r={r} fill="none"
          stroke="#2dd4bf" strokeWidth="8" strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c - (c * percent) / 100}
        />
      </svg>
      <div className="pc-ring-text">{percent}%</div>
    </div>
  );
}

function Swatch({ state }) {
  // The three non-colours reuse the box's own treatment, so the legend and the
  // chart cannot drift apart.
  return <span className={`pc-swatch pc-box is-${state}`} style={{ position: 'relative' }} />;
}

function Box({ box, onOpen }) {
  const { label } = stateLanguage(box.state);
  const fill = box.state === STATES.IN_PROGRESS
    ? `${Math.round((box.fillFraction || 0) * 100)}%`
    : null;

  return (
    <button
      type="button"
      className={`pc-box is-${box.state}${box.readyForAnotherGo ? ' is-again' : ''}`}
      onClick={() => onOpen(box)}
      // Colour is never the only signal, and a square with no text needs this.
      aria-label={`${box.short}. ${label}${box.readyForAnotherGo ? `. ${READY_AGAIN.label}` : ''}`}
      title={`${box.short} - ${label}`}
    >
      {fill && <i style={{ height: fill }} />}
    </button>
  );
}

function Step({ level, onOpen }) {
  const general = level.generalization;
  return (
    <div className="pc-step">
      <span className="pc-step-label">{stepLabel(level.level)}</span>
      <span className="pc-step-boxes">
        {level.boxes.map((b) => <Box key={b.boxId} box={b} onOpen={onOpen} />)}
      </span>
      <span className="pc-step-flag">
        {general && general.verified && (
          // Section 9.3. Green in two different games is a different and much
          // stronger fact than green in one, and a parent will never guess it.
          <span className="pc-badge" title={`Done in ${general.greenGames.length} different games`}>
            ✦ 2 games
          </span>
        )}
      </span>
    </div>
  );
}

function Phase({ phase, isCurrent, open, onToggle, onOpen }) {
  const pct = phase.scoreableBoxes
    ? Math.round((phase.greenBoxes / phase.scoreableBoxes) * 100)
    : 0;

  return (
    <section className={`pc-phase${isCurrent ? ' is-current' : ''}${phase.complete ? ' is-complete' : ''}`}>
      <button
        type="button"
        className="pc-phase-head"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="pc-phase-num">{phase.complete ? '✓' : phase.phase}</span>
        <span className="pc-phase-title">
          <strong>{phase.name}</strong>
          <span>
            {phase.scoreableBoxes
              ? `${phase.greenBoxes} of ${skillCount(phase.scoreableBoxes)}`
              : 'Nothing here your games can measure yet'}
            {isCurrent ? ' · here now' : ''}
          </span>
        </span>
        <span className="pc-phase-chev">{open ? '▲' : '▼'}</span>
      </button>

      <div className="pc-phase-bar"><i style={{ width: `${pct}%` }} /></div>

      {open && (
        <div className="pc-phase-body">
          <p style={{ fontSize: '.8rem', color: '#5b6b80', margin: '0 0 .6rem' }}>
            {phase.description}
          </p>
          {phase.levels.map((l) => (
            <Step key={`${l.phase}.${l.level}`} level={l} onOpen={onOpen} />
          ))}
        </div>
      )}
    </section>
  );
}

function Sheet({ box, model, onClose }) {
  if (!box) return null;
  const { label, blurb } = stateLanguage(box.state);
  const phase = model.phases.find((p) => p.phase === box.phase);

  // Which game would open this one up. Only ever asked for a box nothing the
  // child has played can measure.
  const unlock = box.state === STATES.NOT_ASSESSABLE && model.assessability
    ? (model.assessability.unlockedBy || {})[box.requires] || []
    : [];

  return (
    <div className="pc-scrim" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="pc-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="pc-sheet-grip" />
        <h3>{box.short}</h3>
        <p className="pc-sheet-where">
          {phase ? phase.name : `Phase ${box.phase}`} · {stepLabel(box.level)}
        </p>

        <div className="pc-sheet-state">
          <Swatch state={box.state} />
          <span>
            <strong>{label}</strong>
            <span>{blurb}</span>
          </span>
        </div>

        {box.means && <p className="pc-means">{box.means}</p>}

        {box.readyForAnotherGo && (
          <div className="pc-sheet-note">
            <strong>{READY_AGAIN.label}</strong>
            {READY_AGAIN.blurb}
          </div>
        )}

        {box.state === STATES.OBSERVATIONAL && box.why && (
          <div className="pc-sheet-note" style={{ borderLeftColor: '#c9a75a', background: '#fdf6e8' }}>
            <strong>Why the game cannot score this</strong>
            This one needs eyes, not a microphone. Watch for it yourself when you
            play together.
          </div>
        )}

        {unlock.length > 0 && (
          <div className="pc-sheet-note" style={{ borderLeftColor: '#3E8BFF', background: '#f0f7ff' }}>
            <strong>Try a different game</strong>
            {unlock.map((g) => g.name).join(' or ')} can measure this one.
          </div>
        )}

        {/* The true current window. Never the headline, never hidden. */}
        {box.scoreable && (
          <div className="pc-sheet-facts">
            <div className="pc-fact">
              <span>Right first time</span>
              <strong>
                {box.plays
                  ? `${box.successes} of the last ${box.plays} goes`
                  : 'No goes yet'}
              </strong>
            </div>
            {box.plays > 0 && box.plays < box.windowSize && (
              <div className="pc-fact">
                <span>Goes needed to be sure</span>
                <strong>{box.windowSize - box.plays} more</strong>
              </div>
            )}
          </div>
        )}

        <button type="button" className="pc-sheet-close" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- chart */

export default function ProgressChart({ childId, childName }) {
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openPhases, setOpenPhases] = useState(() => new Set());
  const [selected, setSelected] = useState(null);
  const [legendOpen, setLegendOpen] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    engineApi.getChart(childId)
      .then((data) => { if (live) { setPayload(data); setError(null); } })
      .catch((e) => { if (live) setError(e.message || 'Could not load progress'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [childId]);

  const model = useMemo(() => (payload ? buildChartModel(payload) : null), [payload]);

  // Open the phase they are working on. Everything else stays shut, because
  // ten open cards is 53 rows of squares and nobody scrolls that on a phone.
  useEffect(() => {
    if (model) setOpenPhases(new Set([model.position.phase]));
  }, [model]);

  if (loading) return <div className="pc-state">Loading {childName ? `${childName}'s` : ''} progress…</div>;
  if (error) return <div className="pc-state">{error}</div>;
  if (!model) return null;

  const { summary, position } = model;

  const toggle = (n) => setOpenPhases((prev) => {
    const next = new Set(prev);
    if (next.has(n)) next.delete(n); else next.add(n);
    return next;
  });

  return (
    <div className="pc">
      <div className="pc-summary">
        <Ring percent={summary.percent} />
        <div className="pc-summary-main">
          <h2>{summary.greenBoxes} of {skillCount(summary.scoreableBoxes)}</h2>
          <p>
            {summary.greenBoxes === 0
              ? 'Just getting started.'
              : `${summary.masteredLevels} step${summary.masteredLevels === 1 ? '' : 's'} finished so far.`}
          </p>
          <span className="pc-summary-where">
            Now: {position.phaseName} · {stepLabel(position.level)}
          </span>
        </div>
      </div>

      {summary.generalizedLevels > 0 && (
        <div className="pc-note">
          <strong>✦ Really stuck: {summary.generalizedLevels} step{summary.generalizedLevels === 1 ? '' : 's'}</strong>
          {childName || 'They'} can do these in more than one game. That is the
          difference between knowing something in one place and really having it.
        </div>
      )}

      {summary.notAssessableBoxes > 0 && model.assessability && (
        <div className="pc-note">
          <strong>{summary.notAssessableBoxes} skills your games cannot see yet</strong>
          These are not missed or failed — no game {childName || 'they'} played so
          far measures them. They fill in as you try other games.
        </div>
      )}

      <div className="pc-legend">
        <button
          type="button"
          className="pc-legend-toggle"
          onClick={() => setLegendOpen((v) => !v)}
          aria-expanded={legendOpen}
        >
          What the colours mean
          <span>{legendOpen ? '▲' : '▼'}</span>
        </button>
        {legendOpen && (
          <div className="pc-legend-body">
            {LEGEND.map((state) => {
              const { label, blurb } = stateLanguage(state);
              return (
                <div className="pc-legend-row" key={state}>
                  <Swatch state={state} />
                  <span>
                    <strong>{label}</strong>
                    <span>{blurb}</span>
                  </span>
                </div>
              );
            })}
            <div className="pc-legend-row">
              <span className="pc-swatch pc-box is-green is-again" style={{ position: 'relative' }} />
              <span>
                <strong>{READY_AGAIN.label}</strong>
                <span>{READY_AGAIN.blurb}</span>
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="pc-phases">
        {model.phases.map((p) => (
          <Phase
            key={p.phase}
            phase={p}
            isCurrent={p.phase === position.phase}
            open={openPhases.has(p.phase)}
            onToggle={() => toggle(p.phase)}
            onOpen={setSelected}
          />
        ))}
      </div>

      <Sheet box={selected} model={model} onClose={() => setSelected(null)} />
    </div>
  );
}

export { COLOUR_HEX };
